import { noop } from 'lodash';
import {
  SelectComparator,
  ValueWarningMessage,
  buildColorWarningKey,
} from '@ardoq/perspectives';
import type { SidebarFormattingRuleBodyProperties } from './types';
import { CommonDefaultFilterKeys } from '@ardoq/api-types';
import { RowHeader } from '../../../atoms';
import { FormattingRuleDoubleRowColorAndValueEditors } from './FormattingRuleDoubleRowColorAndValueEditors';
import { FormattingRuleSingleRowColorAndValueEditors } from './FormattingRuleSingleRowColorAndValueEditors';
import { QueryBuilderFilterType } from '@ardoq/query-builder';
import { InvertCheckbox } from './atoms';
import { FormattingRuleColorPickerOnly } from './FormattingRuleColorPickerOnly';
import { FormattingRuleOperator } from '@ardoq/api-types';

/**
 * the list of formatting rule field types that get the fresh compact UI where the color picker is on the same row as the rule value input box.
 *
 * field types not on this list will receive the legacy treatment in the UI.
 */
const SINGLE_ROW_FILTER_TYPES: ReadonlySet<QueryBuilderFilterType> = new Set([
  QueryBuilderFilterType.ASYNC_PARENT,
  QueryBuilderFilterType.ASYNC_COMPONENT_OID,
  QueryBuilderFilterType.ASYNC_REFERENCE_OID,
  QueryBuilderFilterType.ASYNC_SOURCE,
  QueryBuilderFilterType.ASYNC_TARGET,
  QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE,
  QueryBuilderFilterType.STRING,
  QueryBuilderFilterType.DATE,
  QueryBuilderFilterType.DATE_TIME,
  QueryBuilderFilterType.DATE_RANGE,
  QueryBuilderFilterType.NUMBER,
  QueryBuilderFilterType.SELECT_MULTIPLE,
  QueryBuilderFilterType.ASYNC_USER,
]);

const FormattingRuleRowByComponentOrReferenceBody = ({
  rule,
  filter,
  comparators,
  formattingContext,
  colorPaletteAlignment,
}: SidebarFormattingRuleBodyProperties) => {
  const { updateRule, ruleInfo } = formattingContext;
  const { comparator } = rule;
  const { id: filterId, filterType, comparators: filterComparators } = filter;
  const { colorWarnings = {} } = ruleInfo[rule.__id] || {};
  const colorWarningMessage = colorWarnings[buildColorWarningKey(rule.color)];
  const isColorPickerOnly =
    filterId === CommonDefaultFilterKeys.TYPE ||
    filterType === QueryBuilderFilterType.BOOLEAN ||
    comparator === FormattingRuleOperator.IS_EMPTY;
  const isSingleRow = SINGLE_ROW_FILTER_TYPES.has(filterType);

  return (
    <>
      <RowHeader>Condition</RowHeader>
      <SelectComparator
        selectedComparator={comparator}
        comparators={comparators || filterComparators}
        onValueChange={comparator => {
          if (!comparator) return; // shouldn't happen

          updateRule(rule.__id, { comparator });
        }}
        isNewConditionalFormatting={true}
        menuPortalTarget={null}
        filterType={filterType}
      />
      {isColorPickerOnly ? (
        <FormattingRuleColorPickerOnly
          formattingContext={formattingContext}
          rule={rule}
          colorPaletteAlignment={colorPaletteAlignment}
        />
      ) : isSingleRow ? (
        <FormattingRuleSingleRowColorAndValueEditors
          filter={filter}
          formattingContext={formattingContext}
          rule={rule}
          colorPaletteAlignment={colorPaletteAlignment}
        />
      ) : (
        <FormattingRuleDoubleRowColorAndValueEditors
          filter={filter}
          formattingContext={formattingContext}
          rule={rule}
          colorPaletteAlignment={colorPaletteAlignment}
        />
      )}
      <ValueWarningMessage message={colorWarningMessage} />
      {rule.isInverted && <InvertCheckbox isInverted onChange={noop} />}
    </>
  );
};

export { FormattingRuleRowByComponentOrReferenceBody };
